import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gavel, ScanSearch, ShieldCheck, PhoneOff, Users, Clock3, Factory } from "lucide-react";

const PILLARS = [
  { icon: Gavel, k: "a", t: "Post Once, Compare Bids", b: "Users upload their product specs, dielines and vector artwork once. Verified regional printers with idle machine capacity compete in real time through a sealed reverse auction to offer the best wholesale pricing, saving buyers 18% to 34% on average." },
  { icon: ScanSearch, k: "b", t: "Automated Pre-Flight Check", b: "The platform's built-in technical engine instantly audits artwork files for colour profile (CMYK), resolution (300+ DPI) and vector cut-lines, preventing expensive misprints before production even begins." },
  { icon: ShieldCheck, k: "c", t: "100% Secure Escrow", b: "Buyers never have to risk blind 100% advance payments to unknown factories. Funds stay locked safely in an escrow account and are only released to the printer after the order is successfully delivered and verified." },
];

const IMPACT = [
  { icon: Users, t: "No middlemen", b: "Buyers deal with the market directly through one platform." },
  { icon: Clock3, t: "Faster procurement", b: "Quotes in hours, not days of calls and emails." },
  { icon: Factory, t: "Idle press hours, monetised", b: "Commercial printers fill spare capacity efficiently." },
];

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="m-shell" data-testid="about-page">
      <Nav />
      <section className="ab-hero" data-testid="about-hero">
        <p className="eyebrow">ABOUT GOPRINTPACK.COM</p>
        <h1 className="car-h1" data-testid="about-title">India's first tech-driven B2B marketplace for <em>commercial printing</em> &amp; custom packaging.</h1>
        <p className="car-hero-sub" data-testid="about-intro">
          GoPrintPack.com replaces manual RFQ friction with real-time reverse auctions and secure escrow protection.
        </p>
      </section>

      <section className="ab-band" data-testid="about-description">
        <div className="ab-band-copy">
          <p className="eyebrow">WHAT WE DO</p>
          <h2>An intelligent digital hub for every print &amp; packaging need.</h2>
          <p>GoPrintPack.com operates like an intelligent digital hub for all commercial printing and custom packaging needs.</p>
        </div>
        <div className="ab-before" data-testid="about-problem">
          <PhoneOff size={20}/>
          <p>Instead of business owners and procurement teams manually calling or emailing dozens of separate printing factories, and waiting days for inconsistent quotes, GoPrintPack.com brings the entire market onto a single platform.</p>
        </div>
      </section>

      <section className="ab-pillars" data-testid="about-pillars">
        {PILLARS.map((p, i) => (
          <article key={p.k} className="ab-pillar" style={{ animationDelay: `${i * 90}ms` }} data-testid={`about-pillar-${p.k}`}>
            <span className="ab-pillar-k">{p.k}.</span>
            <div className="m-value-icon"><p.icon size={19}/></div>
            <h3>{p.t}</h3>
            <p>{p.b}</p>
          </article>
        ))}
      </section>

      <section className="ab-impact" data-testid="about-impact">
        <p className="ab-impact-lead" data-testid="about-impact-text">
          By digitizing the workflow, GoPrintPack eliminates middlemen, slashes procurement timelines, and helps commercial printers monetize idle press hours efficiently.
        </p>
        <div className="ab-impact-grid">
          {IMPACT.map((x, i) => (
            <div key={x.t} className="ab-impact-item" data-testid={`about-impact-${i}`}>
              <x.icon size={18}/><b>{x.t}</b><span>{x.b}</span>
            </div>
          ))}
        </div>
        <div className="car-hero-cta" style={{ marginTop: 30 }}>
          <Link to="/register" className="m-cta m-cta-lg" data-testid="about-cta-start">Post your first job <ArrowRight size={16}/></Link>
          <Link to="/partner" className="m-outline" data-testid="about-cta-partner">Join as a printer or partner</Link>
        </div>
      </section>

      <footer className="car-foot" data-testid="about-footer">
        <div><img src="/logo.png" alt="GoPrintPack" className="car-foot-logo"/><p>Smart platform for print &amp; packaging procurement.</p></div>
        <div className="car-foot-links">
          <Link to="/">Home</Link><Link to="/careers">Careers</Link><Link to="/contact">Contact</Link><Link to="/terms">Terms</Link><Link to="/privacy">Privacy</Link>
        </div>
        <div className="car-foot-small">© Tejas Industries. GoPrintPack™ is a trademark of Tejas Industries.</div>
      </footer>
    </div>
  );
}

function Nav() {
  return (
    <header className="m-nav" data-testid="about-navbar">
      <Link to="/" className="m-brand" data-testid="about-brand-home"><img src="/logo.png" alt="GoPrintPack" className="m-brand-logo"/></Link>
      <nav className="m-nav-links">
        <Link to="/" data-testid="about-nav-home">Home</Link>
        <Link to="/about" data-testid="about-nav-about">About us</Link>
        <Link to="/contact" data-testid="about-nav-contact">Contact</Link>
        <Link to="/partner" data-testid="about-nav-partner">Partner with us</Link>
      </nav>
      <div className="m-nav-actions">
        <Link to="/login" className="m-ghost" data-testid="about-nav-signin">Sign in</Link>
        <Link to="/register" className="m-cta" data-testid="about-nav-start">Get started <ArrowRight size={14}/></Link>
      </div>
    </header>
  );
}
